import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Boxes, Truck, FileText, UserCheck, ShoppingCart, Users, Store, PackageOpen, BarChart3, Trophy, Bell, BellOff, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pushSupported, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

const ChampionBanner = () => {
  const [champ, setChamp] = useState(null);
  useEffect(() => { api.get("/champions").then(({ data }) => setChamp((data || []).find((c) => (c.status || "announced") === "announced") || null)).catch(() => {}); }, []);
  if (!champ || !champ.user_id) return null;
  const monthName = new Date(champ.month + "-01T00:00:00+07:00").toLocaleDateString("id-ID", { month: "long", year: "numeric", timeZone: "Asia/Jakarta" });
  return (
    <div data-testid="champion-banner" className="rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl bg-amber-400 text-white flex items-center justify-center shrink-0"><Trophy size={22} /></div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Karyawan Terbaik · {monthName}</p>
        <p className="text-base font-extrabold text-slate-900 truncate" style={{fontFamily:'Manrope'}}>{champ.full_name}</p>
        <p className="text-xs text-slate-600">
          +{champ.delta_total} poin · Bonus <b className="text-emerald-700">Rp {Number(champ.bonus).toLocaleString("id-ID")}</b>
          {champ.streak > 1 && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 text-[10px] font-bold">BERTAHAN {champ.streak} BULAN — BONUS NAIK</span>}
        </p>
      </div>
    </div>
  );
};

const PushNotifCard = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const supported = pushSupported();

  const refresh = () => api.get("/push/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { if (supported) refresh(); }, [supported]);
  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      await refresh();
      toast.success("Notifikasi aktif di perangkat ini");
    } catch (e) {
      toast.error(e.message || "Gagal mengaktifkan notifikasi");
    } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      await refresh();
      toast.success("Notifikasi dimatikan di perangkat ini");
    } catch (e) {
      toast.error(e.message || "Gagal mematikan notifikasi");
    } finally { setBusy(false); }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      await api.post("/push/test");
      toast.success("Notifikasi tes dikirim — cek layar HP Anda");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengirim tes");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="push-notif-card" className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${status?.subscribed ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
          {status?.subscribed ? <Bell size={18} /> : <BellOff size={18} />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-900">Notifikasi HP (Gratis)</p>
          <p data-testid="push-notif-status" className="text-xs text-slate-500">
            {status?.subscribed ? `Aktif di ${status.devices} perangkat` : "Nonaktif — aktifkan untuk menerima update di HP ini"}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {status?.subscribed && (
          <Button data-testid="test-notification-btn" variant="outline" size="sm" disabled={busy} onClick={sendTest}>
            <Send size={14} className="mr-1" />Tes
          </Button>
        )}
        {status?.subscribed ? (
          <Button data-testid="disable-notifications-btn" variant="outline" size="sm" disabled={busy} onClick={disable}>Matikan</Button>
        ) : (
          <Button data-testid="enable-notifications-btn" size="sm" disabled={busy} onClick={enable}>Aktifkan</Button>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, icon: Icon, tint, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
    <div className="flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tint}`}><Icon size={18} /></div>
    </div>
    <p className="mt-3 text-3xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>{value}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [myScore, setMyScore] = useState(null);
  const [showAllScore, setShowAllScore] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/stats/dashboard").then(({ data }) => setStats(data));
    }
    api.get("/activities?limit=8").then(({ data }) => setRecent(data));
    api.get("/scores/me").then(({ data }) => setMyScore(data)).catch(() => {});
  }, [user]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-600 font-bold">Ringkasan</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Hi, {user?.full_name?.split(' ')[0]}</h1>
        <p className="mt-1 text-slate-600 text-base">Pantau seluruh operasi gudang dan cabang di sini.</p>
      </div>

      <PushNotifCard />

      <ChampionBanner />

      {myScore && (
        <div data-testid="my-score-card" className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Score Prestasi Anda</p>
            <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Trophy size={18} /></div>
          </div>
          <div className="mt-2 flex items-end gap-2">
            <p className={`text-3xl font-extrabold ${myScore.score >= 100 ? 'text-emerald-600' : myScore.score >= 80 ? 'text-amber-600' : 'text-red-600'}`} style={{fontFamily:'Manrope'}}>{myScore.score}</p>
            <p className="text-xs text-slate-500 pb-1.5">mulai dari 100</p>
          </div>
          <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${myScore.score >= 100 ? 'bg-emerald-500' : myScore.score >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, (myScore.score / 150) * 100)}%` }} />
          </div>
          {(myScore.history || []).length > 0 ? (
            <div className="mt-3">
              <div className="space-y-2">
                {(showAllScore ? myScore.history : myScore.history.slice(0, 3)).map((h) => (
                  <div key={h.id} data-testid={`score-history-${h.id}`} className="flex items-start gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0 ${h.delta > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{h.delta > 0 ? '+' : ''}{h.delta}</span>
                    <div className="min-w-0">
                      <p className="text-xs text-slate-700 font-semibold">{h.reason}</p>
                      <p className="text-[11px] text-slate-400">oleh {h.created_by || 'Sistem'} · {new Date(h.created_at).toLocaleString('id-ID')}</p>
                    </div>
                  </div>
                ))}
              </div>
              {myScore.history.length > 3 && (
                <button data-testid="score-history-toggle" onClick={() => setShowAllScore(!showAllScore)} className="mt-2 text-xs font-bold text-blue-600 hover:underline">
                  {showAllScore ? "Tutup riwayat" : `Lihat semua riwayat (${myScore.history.length})`}
                </button>
              )}
            </div>
          ) : (
            <p className="mt-3 text-xs text-slate-500">Belum ada perubahan poin — pertahankan kinerja Anda.</p>
          )}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat testid="stat-users" label="Karyawan" value={stats.users_count} icon={Users} tint="bg-blue-100 text-blue-700" />
          <Stat testid="stat-branches" label="Cabang" value={stats.branches_count} icon={Store} tint="bg-emerald-100 text-emerald-700" />
          <Stat testid="stat-products" label="Produk" value={stats.products_count} icon={PackageOpen} tint="bg-amber-100 text-amber-700" />
          <Stat testid="stat-attendance" label="Absen Hari Ini" value={stats.attendance_today} icon={UserCheck} tint="bg-violet-100 text-violet-700" />
          <Stat testid="stat-shipments" label="Pengiriman Aktif" value={stats.shipments_pending} icon={Truck} tint="bg-orange-100 text-orange-700" />
          <Stat testid="stat-resi" label="Resi Belum Drop" value={stats.resi_pending} icon={FileText} tint="bg-red-100 text-red-700" />
          <Stat testid="stat-pr" label="Permintaan Barang" value={stats.pr_pending} icon={ShoppingCart} tint="bg-cyan-100 text-cyan-700" />
        </div>
      )}

      {user?.role !== "admin" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {user?.role === "gudang" && (
            <>
              <Link to="/gudang" className="bg-slate-900 text-white rounded-xl p-5 hover:bg-slate-800 transition-colors">
                <PackageOpen size={22} />
                <p className="mt-3 text-lg font-bold" style={{fontFamily:'Manrope'}}>Buka Modul Gudang</p>
                <p className="text-sm text-slate-300">Terima bahan, repack, kirim ke cabang</p>
              </Link>
              <Link to="/absensi" className="bg-blue-600 text-white rounded-xl p-5 hover:bg-blue-700 transition-colors">
                <UserCheck size={22} />
                <p className="mt-3 text-lg font-bold" style={{fontFamily:'Manrope'}}>Absen Masuk / Keluar</p>
                <p className="text-sm text-blue-100">Selfie + GPS lokasi</p>
              </Link>
            </>
          )}
          {user?.role === "keuangan" && (
            <>
              <Link to="/resi" className="bg-slate-900 text-white rounded-xl p-5 hover:bg-slate-800 transition-colors">
                <FileText size={22} />
                <p className="mt-3 text-lg font-bold" style={{fontFamily:'Manrope'}}>Upload & Rekap Resi</p>
                <p className="text-sm text-slate-300">Kirim resi ke cabang, hitung produk otomatis</p>
              </Link>
              <Link to="/laporan" className="bg-blue-600 text-white rounded-xl p-5 hover:bg-blue-700 transition-colors">
                <BarChart3 size={22} />
                <p className="mt-3 text-lg font-bold" style={{fontFamily:'Manrope'}}>Laporan Balance</p>
                <p className="text-sm text-blue-100">Pantau selisih & ketidakseimbangan stok</p>
              </Link>
            </>
          )}
          {user?.role === "cabang" && (
            <>
              <Link to="/cabang-kota" className="bg-slate-900 text-white rounded-xl p-5 hover:bg-slate-800 transition-colors">
                <Truck size={22} />
                <p className="mt-3 text-lg font-bold" style={{fontFamily:'Manrope'}}>Modul Cabang</p>
                <p className="text-sm text-slate-300">Terima stok, upload resi, drop-off</p>
              </Link>
              <Link to="/absensi" className="bg-blue-600 text-white rounded-xl p-5 hover:bg-blue-700 transition-colors">
                <UserCheck size={22} />
                <p className="mt-3 text-lg font-bold" style={{fontFamily:'Manrope'}}>Absen Masuk / Keluar</p>
                <p className="text-sm text-blue-100">Selfie + GPS lokasi</p>
              </Link>
            </>
          )}
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-slate-900" style={{fontFamily:'Manrope'}}>Aktivitas Terkini</h2>
          {user?.role === "admin" && <Link to="/aktivitas" className="text-sm text-blue-600 font-semibold">Lihat semua →</Link>}
        </div>
        <div className="space-y-2">
          {recent.length === 0 && <p className="text-sm text-slate-500">Belum ada aktivitas.</p>}
          {recent.map((a) => (
            <div key={a.id} data-testid={`activity-${a.id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
              {a.photo_id ? (
                <img src={fileUrl(a.photo_id)} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400"><FileText size={20} /></div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{a.type?.replace(/_/g, ' ')}</p>
                <p className="text-sm text-slate-900 font-semibold truncate">{a.notes || a.created_by_name}</p>
                <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString('id-ID')}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
